/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, useState, createContext } from "react";
import { setCurrentIRData } from "../auth/store";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Switch, Route, useRouteMatch } from "react-router-dom";
import Content from "./Content";
import UserInfoCard from "./UserInfoCard";
import { LayoutContext } from "../components/layout";
import { useContext } from "react";
import CycleVisualizer from "../components/CycleVisualizer";
import fetchHelper from "../services/fetchHelper";
import isEqual from "lodash/isEqual";
import { featureCheck } from "../utils/feature";

export const Dashboard2Context = createContext({});

const Dashboard2 = () => {
  const dispatch = useDispatch();
  const { user, headerDetails, mfrData, setMfrData, fetchUser, updateHeaderDetails } =
    useContext(LayoutContext);
  const { addToast } = useToasts();
  const { i } = useParams();
  const [userInfo, setUserInfo] = useState(null);
  const [newInbox, setNewInbux] = useState(null);
  const [showDownload, setShowDownload] = useState(true);
  const [rMonitoring, setRMonitoring] = useState(null);
  const [rMonitoringCycles30, setRMonitoringCycles30] = useState(null);
  const [rMonitoringCycles90, setRMonitoringCycles90] = useState(null);
  const [interrogations, setInterrogations] = useState([]);
  const [showCycles, setShowCycles] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [showDeviceRecallAlert, setShowDeviceRecallAlert] = useState(false);
  const [showLeadAdvisory, setShowLeadAdvisory] = useState(false);
  const [showDeviceAdvisory, setShowDeviceAdvisory] = useState(false);
  let [leadsMessageArray, setLeadsMessageArray] = useState([]);
  let [leadsRecallMessage, setLeadsRecallMessage] = useState([]);
  let [deviceRecallMessage, setDeviceRecallMessage] = useState({});
  let leadsMessages = [];
  const [deviceName, setDeviceName] = useState(null);
  const [deviceModelName, setDeviceModelName] = useState(null);
  const [headerDataPrev, setHeaderDataPrev] = useState(null);

  const start = parseInt(localStorage.getItem("inboxCurrentIndex") || 0);

  const fetchI = async () => {
    if (!navigator.onLine) return (window.location = "/auth/login");
    try {
      const LS = JSON.parse(localStorage.getItem("genToken"));
      const token = LS["token"];
      const parms = { headers: { "Content-Type": "application/json", token } };
      let url = "/patientmanagement/rest/interrogations/" + i;

      if (featureCheck("newInterrogationAPI.enable")) {
        url = "/patientmanagement/rest/v2/interrogations/" + i;
      }

      const r = await fetch(url, parms);
      if (r.ok) {
        const response = await r.text();
        if (response) {
          const j = JSON.parse(response);
          dispatch(setCurrentIRData(j));
          setUserInfo(j);
          setMfrData(j);
          if (featureCheck("newInterrogationAPI.enable")) {
            updateHeaderDetails(j);
          }
        } else {
          addToast("The interrogation does not exist or you do not have access to the interrogation.", {
            appearance: "warning",
            autoDismiss: false,
          });
        }
      } else {
        addToast("API Error: Please check your connection and try again.", {
          appearance: "error",
          autoDismiss: false,
        });
      }
    } catch (error) {
      addToast("API Error: Please check your connection and try again.", {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(error);
      console.log("Connectivity problem.  Please try again later.");
    }
  };

  useEffect(() => {
    if (user) {
      fetchI();
      getNewInbox();
    }
  }, [user]);

  useEffect(() => {
    if (headerDetails) {
      showLeadAdvisoryMessage(headerDetails);
      showDeviceAdvisoryMessage(headerDetails);
    }
  }, [headerDetails]);

  useEffect(() => {
    if (
      !isEqual(headerDetails?.device, headerDataPrev?.device) ||
      !isEqual(headerDetails?.patient?.leads, headerDataPrev?.patient?.leads)
    ) {
      showAlertMessage(headerDetails);
      setHeaderDataPrev(headerDetails);
    }
  }, [headerDetails?.device, headerDetails?.patient?.leads]);

  useEffect(() => {
    updateHeaderDetails(null);
    setMfrData(null);

    if (!featureCheck("newInterrogationAPI.enable")) {
      fetchUser(i);
    }

    return () => {
      updateHeaderDetails(null);
      setMfrData(null);
    };
  }, []);

  let { path } = useRouteMatch();

  const updateUserInfo = (data) => setUserInfo(data);

  const addReportStatement = (rs) => {
    const rss = userInfo.reportStatements || [];
    rss.push(rs);
    setUserInfo({ ...userInfo, reportStatements: [...rss] });
  };

  const removeReportStatement = (rs) => {
    const rss = userInfo.reportStatements || [];
    rss.splice(rss.map((i) => i.cstId).indexOf(rs.id), 1);
    setUserInfo({ ...userInfo, reportStatements: [...rss] });
  };

  const showAlertMessage = async (userInfo) => {
    let lead = null;
    let leadRecallMessages = [];
    try {
      for (let i = 0; i < userInfo.patient.leads.length; i++) {
        lead = userInfo.patient.leads[i];
        if (lead.status == "IMPLANTED" || lead.status == undefined) {
          const urlParams = {
            manufacturerName: lead.manufacturerName,
            leadType: lead.leadType ? lead.leadType : "-",
            modelName: lead.leadModel ? lead.leadModel : "-",
            serialNumber: lead.serialNumber ? lead.serialNumber : "-",
            implantedDate: lead.implantedDateStr,
          };
          try {
            const leadRecallResponse = await fetchHelper.get({
              url: `/patientmanagement/rest/gen-recall-service/recall/lead`,
              urlParams: urlParams,
            });
            if (leadRecallResponse.hasRecall) {
              setShowAlert(true);
              const name = `${lead.manufacturerName} ${lead.leadModel ? lead.leadModel : "undefined"}`;
              leadRecallMessages.push({ url: leadRecallResponse.urls[0], leadName: name });
            }
          } catch (err) {
            console.log("There is an error getting a lead's recall");
          }
        }
      }
      setLeadsRecallMessage(leadRecallMessages);

      if (userInfo.device.status == "IMPLANTED" || userInfo.device.status == undefined) {
        const urlParams = {
          manufacturerName: userInfo.device.manufacturerName,
          deviceType: userInfo.device.deviceType ? userInfo.device.deviceType : "-",
          modelName: userInfo.device.modelName ? userInfo.device.modelName : "-",
          serialNumber: userInfo.device.serialNumber ? userInfo.device.serialNumber : "-",
          implantedDate: userInfo.device.implantedDateStr,
        };

        if (urlParams.implantedDate == null) {
          delete urlParams.implantedDate;
        }

        const deviceRecallResponse = await fetchHelper.get({
          url: `/patientmanagement/rest/gen-recall-service/recall/device`,
          urlParams: urlParams,
        });
        if (deviceRecallResponse.hasRecall) {
          setShowDeviceRecallAlert(true);
          let name = userInfo.device.manufacturerName;
          if (userInfo.device.modelName == undefined || userInfo.device.modelName == "") {
            name = name + " undefined";
          } else {
            name = name + " " + userInfo.device.modelName;
          }
          setDeviceRecallMessage({ url: deviceRecallResponse.urls[0], name: name });
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const showLeadAdvisoryMessage = (userInfo) => {
    let lead = null;
    for (let i = 0; i < userInfo.patient.leads.length; i++) {
      lead = userInfo.patient.leads[i];
      if ((lead.status == "IMPLANTED" || lead.status == undefined) && lead.advisory == true) {
        if (lead.leadModel == "" || lead.leadModel == undefined) {
          leadsMessages.push(lead.manufacturerName + " undefined");
        } else {
          leadsMessages.push(lead.manufacturerName + " " + lead.leadModel);
        }
      }
    }
    setLeadsMessageArray(leadsMessages);
    leadsMessages.length > 0 ? setShowLeadAdvisory(true) : setShowLeadAdvisory(false);
  };

  const showDeviceAdvisoryMessage = (userInfo) => {
    if (
      (userInfo.device.status == "IMPLANTED" || userInfo.device.status == undefined) &&
      userInfo.device.advisory == true
    ) {
      setDeviceName(userInfo.device.manufacturerName);
      if (userInfo.device.modelName == undefined || userInfo.device.modelName == "") {
        setDeviceModelName("undefined");
      } else {
        setDeviceModelName(userInfo.device.modelName);
      }
      setShowDeviceAdvisory(true);
      return;
    }
    setShowDeviceAdvisory(false);
  };

  const getNewInbox = async () => {
    try {
      let searchParameters = localStorage.getItem("interrogationFilter");
      let urlSearch;
      if (searchParameters == null) {
        const filter = {
          "readingPhysician.id": { key: "readingPhysician.id", com: "$eq", value: `${user.id}` },
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "PRELIMINARY" },
        };
        const where = [];
        Object.keys(filter).map((f) => {
          const data = filter[f];
          where.push({ [data.key]: { [data.com]: data.value } });
        });

        const urlParams = {
          where: JSON.stringify(where),
          match: "AND",
          matchInner: "OR",
          sort: "flagSort ,interrogationDate",
        };
        urlSearch = new URLSearchParams(urlParams);
      } else {
        urlSearch = searchParameters;
      }
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/interrogations/reportListView?${urlSearch}`,
        headers: {
          "X-Range": `items=${start}-${start + 2}`,
          Range: `items=${start}-${start + 2}`,
        },
      });

      setNewInbux(data);
    } catch (err) {
      console.log(err);
    }
  };

  const filterDeviceInterrogations = () => {
    const deviceId = headerDetails?.device?.id;
    return deviceId ? interrogations.filter((item) => item?.device.id == deviceId) : interrogations;
  };

  const fetchRMonitoring = async () => {
    try {
      const deviceId = headerDetails?.device?.id;
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/rmonitoring/findByDeviceId/${deviceId}`,
        // urlDev: "/jsons/rmonitoring.json"
      });
      setRMonitoring(data);
      if (data?.nintyDayFlag == "ACTIVE") fetchCycles(data?.nintyDaySDate, 90);
      if (data?.thirtyDayFlag == "ACTIVE") fetchCycles(data?.thirtyDaySDate, 30);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCycles = async (startCycleDate, cycleType) => {
    try {
      const data = await fetchHelper.get({
        url:
          "/patientmanagement/rest/rmonitoring/cycles?startCycleDate=" +
          startCycleDate +
          "&cycleType=" +
          cycleType +
          "",
      });
      if (cycleType == 30) {
        setRMonitoringCycles30(data);
      }
      if (cycleType == 90) {
        setRMonitoringCycles90(data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInterrogations = async () => {
    try {
      const urlParams = {
        where: JSON.stringify({ "patient.id": { $eq: headerDetails?.patient.id } }),
        match: "AND",
        matchInner: "OR",
        sort: "-interrogationDate",
      };

      const urlSearch = new URLSearchParams(urlParams);

      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/interrogations/getNewInbox?" + urlSearch,
      });
      setInterrogations(data);
    } catch (err) {
      console.log(err);
    }
  };

  const onShowCycles = () => {
    setShowCycles((prev) => !prev);
  };

  useEffect(() => {
    if (showCycles && initialLoad) {
      fetchRMonitoring();
      fetchInterrogations();
      setInitialLoad(false);
    }
  }, [showCycles]);

  return (
    <Dashboard2Context.Provider
      value={{
        fetchI,
        userInfo,
        newInbox,
        updateUserInfo,
        addReportStatement,
        removeReportStatement,
        setShowDownload,
        mfrData,
        setMfrData,
      }}
    >
      <Switch>
        <Route exact path={path}>
          <>
            <div className="content pt-info" style={{ marginBottom: 0 }}>
              <div className="row row-stretch" style={{ position: "relative" }}>
                <UserInfoCard
                  userInfo={user}
                  headerInfo={headerDetails}
                  showDownload={showDownload}
                  showCycles={onShowCycles}
                />
              </div>
            </div>
            {showCycles ? (
              <div className="row rowStretch cycle-row">
                <div className="col col-s-100 col100">
                  <div className="scheduler">
                    <CycleVisualizer
                      startDate={rMonitoring?.thirtyDaySDate}
                      interrogations={filterDeviceInterrogations()}
                      rMonitoringCycles={rMonitoringCycles30}
                    />
                    <CycleVisualizer
                      startDate={rMonitoring?.nintyDaySDate}
                      interrogations={filterDeviceInterrogations()}
                      cycle={90}
                      rMonitoringCycles={rMonitoringCycles90}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            {showAlert ? (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <button
                  type="button"
                  className="close "
                  data-dismiss="alert"
                  aria-label="Close"
                  onClick={() => setShowAlert(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                {leadsRecallMessage.map((item, i) => (
                  <div key={i}>
                    <strong>{item.leadName}</strong> may have been recalled by the FDA. For further details,
                    please select the link.
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {" "}
                      Click for more info
                    </a>
                  </div>
                ))}
              </div>
            ) : null}
            {showDeviceRecallAlert ? (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>{deviceRecallMessage.name}</strong> may have been recalled by the FDA. For further
                details, please select the link.
                <button
                  type="button"
                  className="close "
                  data-dismiss="alert"
                  aria-label="Close"
                  onClick={() => setShowDeviceRecallAlert(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                <a href={deviceRecallMessage.url} target="_blank" rel="noreferrer">
                  {" "}
                  Click for more info
                </a>
              </div>
            ) : null}

            {showLeadAdvisory ? (
              <div className="alert alert-advisory alert-dismissible fade show" role="alert">
                <button
                  type="button"
                  className="close "
                  data-dismiss="alert"
                  aria-label="Close"
                  onClick={() => setShowLeadAdvisory(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                {leadsMessageArray.map((item, i) => (
                  <div key={i}>
                    <strong>{item}</strong> is identified as part of a manufacturer advisory.
                  </div>
                ))}
              </div>
            ) : null}

            {showDeviceAdvisory ? (
              <div className="alert alert-advisory alert-dismissible fade show" role="alert">
                <strong>
                  {deviceName} {deviceModelName}
                </strong>{" "}
                is identified as part of a manufacturer advisory.
                <button
                  type="button"
                  className="close "
                  data-dismiss="alert"
                  aria-label="Close"
                  onClick={() => setShowDeviceAdvisory(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            ) : null}

            {headerDetails ? <Content userInfo={userInfo} /> : null}
          </>
        </Route>
      </Switch>
    </Dashboard2Context.Provider>
  );
};

export default Dashboard2;
